import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { StoreRow, VisitTimeRow } from "../../types/pivot.type";
import { PivotExtraColumnGroup } from "./PivotGroupedTable";
import { fmtClockRange, fmtMinutes } from "./routeTimeFormat";
import { PV } from "./pivotTokens";
import { PIVOT_ROUTE_TIME_MAX_DAYS, ROUTE_TIME_HO_SPAN_MSG, RouteTimeSpanBlock } from "./pivotLimits";

/** `blocked` — the range is past the endpoint's 31-day cap, so it was never asked. */
export type VisitTimesStatus = "loading" | "ok" | "error" | "blocked";

export const VISIT_TIMES_SPAN_MSG = `เวลาเข้าร้านดูได้ครั้งละไม่เกิน ${PIVOT_ROUTE_TIME_MAX_DAYS} วัน`;

/** The note for a skipped visit-times call, by which span cap it hit. */
export const visitTimesBlockedMessage = (block: RouteTimeSpanBlock) =>
  block === "ho" ? ROUTE_TIME_HO_SPAN_MSG : VISIT_TIMES_SPAN_MSG;

const Dash = () => (
  <Box component="span" sx={{ color: PV.tx3 }}>
    –
  </Box>
);

/**
 * The "เวลา (PWA)" band on the สายส่ง tab (board A3). Every figure is a median over the days
 * in range, and only PWA routes have one — a store with no row renders "–", never 0.
 */
export function visitTimeColumnGroup(
  lookup: (customerCode: string, routeCode?: string | null) => VisitTimeRow | undefined,
  status: VisitTimesStatus,
  blockedMessage: string = VISIT_TIMES_SPAN_MSG,
): PivotExtraColumnGroup<StoreRow> {
  const cell = (row: StoreRow, render: (t: VisitTimeRow) => React.ReactNode) => {
    if (status === "loading") return <Skeleton variant="text" width={40} sx={{ ml: "auto" }} />;
    const t = lookup(row.customer_code, row.route_code);
    return t ? render(t) : <Dash />;
  };

  return {
    title: (
      <>
        เวลา (PWA)
        <Box
          component="span"
          sx={{
            ml: 0.75,
            fontSize: 11,
            px: 0.75,
            py: "1px",
            borderRadius: "999px",
            bgcolor: PV.card,
            color: PV.indigo,
          }}
        >
          ใหม่
        </Box>
      </>
    ),
    columns: [
      {
        key: "arrived_median",
        label: "ถึงร้าน",
        align: "left",
        render: (row) =>
          cell(row, (t) =>
            t.arrived_median ? (
              <Box component="span" sx={{ fontWeight: 600 }}>
                {t.arrived_median}
              </Box>
            ) : (
              <Dash />
            ),
          ),
      },
      {
        key: "arrived_range",
        label: "ช่วงเวลาถึง",
        align: "left",
        render: (row) =>
          cell(row, (t) => (
            <Box component="span" sx={{ color: PV.tx2, fontSize: 12.5 }}>
              {fmtClockRange(t.arrived_p25, t.arrived_p75)}
            </Box>
          )),
      },
      {
        key: "cycle_median_min",
        label: "รอบต่อร้าน",
        align: "right",
        render: (row) => cell(row, (t) => fmtMinutes(t.cycle_median_min)),
      },
      {
        key: "app_median_min",
        label: "ทำรายการ",
        align: "right",
        render: (row) => cell(row, (t) => fmtMinutes(t.app_median_min)),
      },
      {
        key: "flag_count",
        label: "ธง",
        align: "right",
        render: (row) =>
          cell(row, (t) =>
            t.flag_count > 0 ? (
              <Box
                component="span"
                title="ครั้งที่เข้าร้านนี้แล้วมีธง ช่วงห่างเกิน / ทำรายการนาน"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  px: 1,
                  py: "1px",
                  borderRadius: "999px",
                  bgcolor: PV.leftoverTint,
                  color: PV.leftover,
                }}
              >
                {t.flag_count}
              </Box>
            ) : (
              <Dash />
            ),
          ),
      },
    ],
    renderGroup: (g) => {
      if (status === "loading") return <Skeleton variant="text" width={120} />;
      if (status === "error") return "โหลดข้อมูลเวลาไม่สำเร็จ";
      if (status === "blocked") return blockedMessage;
      const withTimes = g.rows.filter((r) => lookup(r.customer_code, r.route_code)).length;
      return withTimes === 0
        ? "ไม่มีข้อมูลเวลา (สายนี้ยังไม่ใช้ PWA)"
        : `มีเวลา PWA ${withTimes}/${g.rows.length} ร้าน`;
    },
  };
}
